// Runtime full-page translation engine (public site only).
// Walks visible text nodes, translates via the backend (Google v2, cached), and
// keeps new/dynamic content translated via a debounced MutationObserver.
import api from './api';

const originalValue = new WeakMap();   // node -> canonical English string
const translatedValue = new WeakMap(); // node -> exact string we last wrote
const nodeLang = new WeakMap();        // node -> language currently displayed
const sessionCache = new Map();        // `${lang}\u0000${source}` -> translation

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'SVG']);

let currentLang = 'en';
let observer = null;
let debounce = null;

const hasLetters = (s) => /[A-Za-z\u00C0-\u024F\u0370-\u1FFF\u0900-\u0DFF\u3040-\u9FFF]/.test(s);

const skip = (parent) => {
  if (!parent || !parent.tagName) return true;
  if (SKIP_TAGS.has(parent.tagName)) return true;
  if (parent.closest && parent.closest('[data-no-translate]')) return true;
  return false;
};

function collect(root, lang) {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (skip(node.parentElement)) return NodeFilter.FILTER_REJECT;
      const v = node.nodeValue;
      if (!v || !v.trim() || !hasLetters(v)) return NodeFilter.FILTER_REJECT;
      const changedByReact = translatedValue.has(node) && node.nodeValue !== translatedValue.get(node);
      if (nodeLang.get(node) === lang && !changedByReact) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

async function fetchMissing(sources, lang) {
  const CHUNK = 90;
  for (let i = 0; i < sources.length; i += CHUNK) {
    const chunk = sources.slice(i, i + CHUNK);
    try {
      const { data } = await api.post('/translate', { texts: chunk, target: lang, source: 'en' });
      (data.translations || []).forEach((t, idx) => sessionCache.set(`${lang}\u0000${chunk[idx]}`, t));
    } catch (e) {
      // On failure leave text untranslated; do not throw so the page stays usable.
      break;
    }
  }
}

async function translateNodes(nodes, lang) {
  const need = new Set();
  nodes.forEach((node) => {
    const cur = node.nodeValue;
    const showingOurTranslation = translatedValue.get(node) === cur && originalValue.has(node);
    if (!showingOurTranslation) originalValue.set(node, cur); // fresh English (first time or React update)
    const src = originalValue.get(node).trim();
    if (!sessionCache.has(`${lang}\u0000${src}`)) need.add(src);
  });
  if (need.size) await fetchMissing([...need], lang);
  if (currentLang !== lang) return; // language changed mid-flight; abort writes
  nodes.forEach((node) => {
    const orig = originalValue.get(node);
    if (!orig) return;
    const src = orig.trim();
    const t = sessionCache.get(`${lang}\u0000${src}`);
    if (t != null && t !== '') {
      const newVal = orig.replace(src, t);
      node.nodeValue = newVal;
      translatedValue.set(node, newVal);
      nodeLang.set(node, lang);
    }
  });
}

// Repeat passes so content that renders during a write also gets translated.
async function translateAll(lang, maxPasses = 4) {
  for (let p = 0; p < maxPasses; p += 1) {
    const nodes = collect(document.body, lang);
    if (!nodes.length) break;
    await translateNodes(nodes, lang);
    if (currentLang !== lang) return;
  }
}

function restore() {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = walker.nextNode())) {
    if (originalValue.has(n) && nodeLang.get(n) !== 'en') {
      n.nodeValue = originalValue.get(n);
      nodeLang.set(n, 'en');
      translatedValue.delete(n);
    }
  }
}

function startObserver() {
  if (observer) return;
  observer = new MutationObserver(() => {
    if (currentLang === 'en') return;
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(async () => {
      const lang = currentLang;
      if (lang === 'en') return;
      stopObserver();
      await translateAll(lang);
      if (currentLang === lang) startObserver();
    }, 350);
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

function stopObserver() {
  if (observer) { observer.disconnect(); observer = null; }
  if (debounce) { clearTimeout(debounce); debounce = null; }
}

export async function applyLanguage(lang) {
  currentLang = lang || 'en';
  stopObserver();
  if (currentLang === 'en') { restore(); return; }
  await translateAll(currentLang);
  if (currentLang !== 'en') startObserver();
}

export const getCurrentLang = () => currentLang;

export const LANGUAGES = [
  ['en', 'English'], ['hi', 'हिन्दी'], ['es', 'Español'], ['fr', 'Français'],
  ['de', 'Deutsch'], ['it', 'Italiano'], ['pt', 'Português'], ['ru', 'Русский'],
  ['zh', '中文'], ['ja', '日本語'], ['te', 'తెలుగు'], ['gu', 'ગુજરાતી'],
];
